import { useCallback } from 'react';

import { Trans, useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { toast } from 'sonner';

import { MODAL_DELETE } from '@/components/organisms/Modals/ModalConductor';
import useMembers from '@/hooks/useMembers';
import { useRoles } from '@/hooks/useRoles';
import { useTeams } from '@/hooks/useTeams';
import { popModal, pushModal } from '@/redux/modals/actions';
import { Role } from '@/shared/types/role';

export const useDeleteRoleModal = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { deleteRole } = useRoles();
  const { members } = useMembers();
  const { teams } = useTeams();

  const isAssigned = useCallback(
    (roleId: string) =>
      (members ?? []).some((member) => member.role_ids?.includes(roleId)) ||
      teams.some((team) => team.role_ids?.includes(roleId)),
    [members, teams]
  );

  return useCallback(
    (role: Pick<Role, 'role_id' | 'name'>, opts: { onSuccess?: () => void } = {}) => {
      if (isAssigned(role.role_id)) {
        toast.error(t('roles.delete_assigned_error'));
        return;
      }

      dispatch(
        pushModal(MODAL_DELETE, {
          title: t('roles.delete_role'),
          subtitle: <Trans i18nKey="roles.delete_subtitle" values={[role.name]} />,
          confirm: true,
          onDelete: () => {
            deleteRole(
              { role_id: role.role_id },
              {
                onSuccess: () => {
                  dispatch(popModal());
                  opts.onSuccess?.();
                },
                onError: () => dispatch(popModal()),
              }
            );
          },
        })
      );
    },
    [deleteRole, dispatch, isAssigned, t]
  );
};
